import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const BathroomOffersPage = () => {
  const [selectedPackage, setSelectedPackage] = useState<number | null>(null);

  const packages = [
    {
      name: 'Essential Bathroom',
      price: '€3,500',
      description: 'Perfect starter package for basic bathroom needs',
      features: [
        'Standard toilet installation',
        'Basic washbasin with cabinet',
        'Shower tray and screen',
        'Chrome mixer taps',
        'Basic tiling (up to 20m²)',
        'Standard lighting',
        'Professional installation',
        '1-year warranty',
      ],
      image: 'https://public.readdy.ai/ai/img_res/b188c8948e3c486118036d9e0fde70f6.jpg',
    },
    {
      name: 'Comfort Bathroom',
      price: '€5,500',
      description: 'Enhanced comfort with quality fixtures and finishes',
      features: [
        'Premium toilet with soft-close seat',
        'Designer washbasin with storage',
        'Walk-in shower enclosure',
        'Thermostatic shower system',
        'Quality ceramic tiling (up to 30m²)',
        'LED mirror with demister',
        'Heated towel rail',
        'Professional installation',
        '2-year warranty',
      ],
      popular: true,
      image: 'https://readdy.ai/api/search-image?query=comfortable%20modern%20bathroom%20with%20premium%20fixtures%20designer%20washbasin%20walk-in%20shower%20quality%20tiles%20heated%20towel%20rail%20contemporary%20design%20light%20elegant%20colors&width=800&height=600&seq=bath-pkg-002&orientation=landscape',
    },
    {
      name: 'Luxury Bathroom',
      price: '€8,500',
      description: 'Premium package with high-end fixtures and finishes',
      features: [
        'Designer wall-hung toilet',
        'Luxury vanity unit with double basin',
        'Frameless glass shower enclosure',
        'Rainfall shower with body jets',
        'Premium porcelain tiling (up to 40m²)',
        'Underfloor heating',
        'Smart LED lighting system',
        'Designer heated towel rail',
        'Professional installation',
        '5-year warranty',
      ],
      image: 'https://readdy.ai/api/search-image?query=luxury%20high-end%20bathroom%20with%20designer%20fixtures%20double%20basin%20vanity%20frameless%20shower%20rainfall%20system%20premium%20tiles%20underfloor%20heating%20elegant%20sophisticated%20design%20light%20colors&width=800&height=600&seq=bath-pkg-003&orientation=landscape',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="bg-primary text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
                Bathroom Offers
              </h1>
              <p className="text-lg sm:text-xl text-gray-300 leading-relaxed">
                Choose from our carefully curated bathroom packages designed to suit every budget and style. All packages include professional installation and quality guarantees.
              </p>
            </div>
          </div>
        </section>

        {/* Packages Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {packages.map((pkg, index) => (
                <div
                  key={index}
                  className={`relative bg-white rounded-2xl overflow-hidden transition-all duration-300 ${
                    pkg.popular
                      ? 'border-2 border-accent shadow-2xl scale-105'
                      : 'border border-gray-200 shadow-lg hover:shadow-xl'
                  }`}
                >
                  {pkg.popular && (
                    <div className="absolute top-0 right-0 bg-accent text-white px-4 py-1 text-xs font-semibold rounded-bl-lg whitespace-nowrap">
                      MOST POPULAR
                    </div>
                  )}

                  {/* Package Image */}
                  <div className="aspect-[4/3] w-full h-full overflow-hidden">
                    <img
                      src={pkg.image}
                      alt={pkg.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>

                  {/* Package Content */}
                  <div className="p-8">
                    <h3 className="text-2xl font-bold text-primary mb-2">
                      {pkg.name}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">{pkg.description}</p>
                    <div className="mb-6">
                      <span className="text-4xl font-bold text-accent">{pkg.price}</span>
                      <span className="text-gray-500 text-sm ml-2">starting from</span>
                    </div>

                    {/* Features List */}
                    <ul className="space-y-3 mb-8">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start text-sm">
                          <i className="ri-check-line text-accent text-lg mr-3 flex-shrink-0 mt-0.5"></i>
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <button
                      onClick={() => setSelectedPackage(index)}
                      className={`w-full py-3 rounded-lg font-semibold transition-colors whitespace-nowrap ${
                        pkg.popular
                          ? 'bg-accent hover:bg-accent-dark text-white'
                          : 'bg-primary hover:bg-primary-dark text-white'
                      }`}
                    >
                      Request Quote
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Additional Services */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-primary mb-4">
                Additional Services
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                Customize your bathroom package with our additional services
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-paint-brush-line', title: 'Painting & Decorating', price: 'From €500' },
                { icon: 'ri-lightbulb-line', title: 'Electrical Work', price: 'From €300' },
                { icon: 'ri-water-flash-line', title: 'Waterproofing', price: 'From €400' },
                { icon: 'ri-hammer-line', title: 'Structural Changes', price: 'Custom Quote' },
              ].map((service, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-shadow text-center"
                >
                  <div className="w-14 h-14 flex items-center justify-center bg-accent/10 rounded-full mx-auto mb-4">
                    <i className={`${service.icon} text-2xl text-accent`}></i>
                  </div>
                  <h3 className="font-bold text-primary mb-2">{service.title}</h3>
                  <p className="text-accent text-sm font-semibold">{service.price}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-primary mb-6">
              Need a Custom Solution?
            </h2>
            <p className="text-gray-600 text-lg mb-8">
              Every bathroom is unique. Contact us to discuss your specific requirements and get a personalized quote.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/#contact"
                className="inline-flex items-center justify-center space-x-2 bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-lg font-medium transition-colors whitespace-nowrap"
              >
                <span>Get Custom Quote</span>
                <i className="ri-arrow-right-line"></i>
              </a>
              <a
                href="tel:+353873719751"
                className="inline-flex items-center justify-center space-x-2 bg-white border-2 border-primary text-primary hover:bg-primary hover:text-white px-8 py-4 rounded-lg font-medium transition-colors whitespace-nowrap"
              >
                <i className="ri-phone-line"></i>
                <span>Call Now</span>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BathroomOffersPage;